import {
  isValidReferenceCode,
  type Order,
  type OrderItem,
  type OrderTotals,
} from '@planner/shared';
import {
  doorsOrderFingerprint,
  parseDoorsDesign,
  type DoorsDesignV1,
  type HistoricalSizeConfirmation,
} from '@planner/shared/doors';
import { apiBaseUrl, region } from './env.js';

export class CartApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
  }
}

export interface SaveDoorCart {
  region: string;
  brand: string;
  design: DoorsDesignV1;
  items: OrderItem[];
  totals: OrderTotals;
  baseReference?: string;
  editingLineId?: string;
  historicalSizeConfirmation?: HistoricalSizeConfirmation;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOrderItem(value: unknown): value is OrderItem {
  return (
    isRecord(value) &&
    typeof value.sku === 'string' &&
    typeof value.description === 'string' &&
    Number.isSafeInteger(value.quantity) &&
    typeof value.unitPrice === 'number' &&
    Number.isFinite(value.unitPrice) &&
    (value.options === undefined ||
      (isRecord(value.options) &&
        Object.values(value.options).every((item) => typeof item === 'string')))
  );
}

function hasOptionalStrings(value: Record<string, unknown>, fields: readonly string[]): boolean {
  return fields.every((key) => value[key] === undefined || typeof value[key] === 'string');
}

function hasFiniteNumbers(value: Record<string, unknown>, fields: readonly string[]): boolean {
  return fields.every((key) => typeof value[key] === 'number' && Number.isFinite(value[key]));
}

function isOrder(value: unknown): value is Order {
  if (
    !isRecord(value) ||
    typeof value.referenceCode !== 'string' ||
    !isValidReferenceCode(value.referenceCode) ||
    value.region !== region.code ||
    typeof value.brand !== 'string' ||
    (value.status !== 'draft' && value.status !== 'submitted' && value.status !== 'emailed') ||
    !Array.isArray(value.items) ||
    !value.items.every(isOrderItem) ||
    !isRecord(value.customer) ||
    !hasOptionalStrings(value.customer, ['name', 'email', 'phone', 'postcode']) ||
    !isRecord(value.totals) ||
    !hasFiniteNumbers(value.totals, ['subtotal', 'tax', 'total']) ||
    typeof value.totals.currency !== 'string' ||
    !isRecord(value.design) ||
    !isRecord(value.channel) ||
    (value.channel.type !== 'direct' &&
      !(
        value.channel.type === 'bunnings' &&
        typeof value.channel.storeId === 'string' &&
        (value.channel.storeName === undefined || typeof value.channel.storeName === 'string')
      )) ||
    (value.doorLineIds !== undefined &&
      (!Array.isArray(value.doorLineIds) ||
        !value.doorLineIds.every((id: unknown) => typeof id === 'string'))) ||
    typeof value.createdAt !== 'string' ||
    typeof value.updatedAt !== 'string' ||
    (value.appVersion !== undefined && typeof value.appVersion !== 'string')
  ) {
    return false;
  }
  return parseDoorsDesign(value.design) !== undefined;
}

async function readCartResponse(response: Response, uncertainOnInvalid: boolean): Promise<Order> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new CartApiError(
      uncertainOnInvalid
        ? 'The save response could not be confirmed. Your draft and previous Cart have been kept.'
        : 'The saved Cart response was invalid.',
      uncertainOnInvalid ? 'uncertain' : 'invalid_response',
    );
  }
  if (!response.ok) {
    const message =
      isRecord(body) && typeof body.error === 'string' ? body.error : 'The Cart request failed.';
    const code = isRecord(body) && typeof body.code === 'string' ? body.code : 'request_failed';
    if (uncertainOnInvalid && response.status >= 500) {
      throw new CartApiError(
        `The save could not be confirmed. ${message} Your draft and previous Cart have been kept.`,
        'uncertain',
      );
    }
    throw new CartApiError(message, code);
  }
  if (
    !isRecord(body) ||
    !isOrder(body.order) ||
    (uncertainOnInvalid &&
      (![200, 201].includes(response.status) || body.referenceCode !== body.order.referenceCode))
  ) {
    throw new CartApiError(
      uncertainOnInvalid
        ? 'The save response could not be confirmed. Your draft and previous Cart have been kept.'
        : 'The saved Cart response was invalid.',
      uncertainOnInvalid ? 'uncertain' : 'invalid_response',
    );
  }
  return body.order;
}

export async function fetchCart(reference: string): Promise<Order> {
  const response = await fetch(`${apiBaseUrl}/orders/${encodeURIComponent(reference)}`);
  const order = await readCartResponse(response, false);
  if (order.referenceCode !== reference) {
    throw new CartApiError('The saved Cart reference did not match.', 'invalid_response');
  }
  return order;
}

export async function saveDoorCart(input: SaveDoorCart): Promise<Order> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch {
    throw new CartApiError(
      'The save could not be confirmed. Your draft and previous Cart have been kept.',
      'uncertain',
    );
  }
  const order = await readCartResponse(response, true);
  if (
    order.status !== 'draft' ||
    order.brand !== input.brand ||
    doorsOrderFingerprint(order.design, order.items) !==
      doorsOrderFingerprint(input.design, input.items) ||
    order.totals.subtotal !== input.totals.subtotal ||
    order.totals.tax !== input.totals.tax ||
    order.totals.total !== input.totals.total ||
    order.totals.currency !== input.totals.currency
  ) {
    throw new CartApiError(
      'The save response could not be confirmed. Your draft and previous Cart have been kept.',
      'uncertain',
    );
  }
  return order;
}
